import React from "react";
import CardHeader from "@mui/material/CardHeader";
import HistoryIcon from "@mui/icons-material/History";
import Button from "@mui/material/Button";
import NoteDialog from "../../Dialogs/NoteDialog";
import {AnalystComment} from "../../Shared/AnalystCommentsEditor/AnalystCommentsEditor";
import {sendAnalystCommentsToDealerTrack} from "../../../store/application/actions";
import ErrorDialog from "../../Dialogs/ErrorDialog";
import Divider from "@mui/material/Divider";
import CardContent from "@mui/material/CardContent";
import DealerServiceHistoryListView from "../DealerServiceHistoryListView/DealerServiceHistoryListView";
import {sort} from "ramda";
import {IOrderableType} from "../../../Interfaces/Common/IDealerServiceAppHistory";
import Card from "@mui/material/Card";
import {IApplication} from "../../../Interfaces/Application/IApplication";
import {useDialog} from "../../../providers/DialogProvider";
import {ApplicationDispatch} from "../../../store/application/types";
import {hasValue} from "@amsanalytics/react-shared-library";
import {getAssignedEmployeeByRoleAndSequence} from "../../../utils/appUtils";
import {Constants} from "../../../utils/constants";
import {IApplicationAssignment} from "../../../Interfaces/Application/IApplicationAssignment";
import {useSelector} from "react-redux";
import { AppState } from '../../../store/rootReducer';

export interface IndirectApplicationHistoryCardProps {
  currentApp: IApplication;
  dispatch: ApplicationDispatch;
}

export const IndirectApplicationHistoryCard = (props: IndirectApplicationHistoryCardProps) => {
  const [openDialog, closeDialog] = useDialog();
  const dealerServiceAppHistoryItems = useSelector((state: AppState) => state.applications.dealerServiceAppHistoryItems);
  const applicationAssignments: IApplicationAssignment[] = useSelector((state: AppState) => state.applications.editingApp?.applicationAssignments) ?? [];

  const validatePreDealertrackComments = (): string[] => {
    const errors: string[] = [];

    if (!hasValue(getAssignedEmployeeByRoleAndSequence(applicationAssignments, Constants.roles.underwriterId, 1)?.assignedToId)) {
      errors.push('Underwriter is required');
    }

    return errors;
  };

  const historyItems: IOrderableType[] = [
    ...dealerServiceAppHistoryItems.applicationsReceived,
    ...dealerServiceAppHistoryItems.acknowledgementsReceived,
    ...dealerServiceAppHistoryItems.commentsReceived,
    ...dealerServiceAppHistoryItems.decisionsSent,
    ...dealerServiceAppHistoryItems.commentsSent,
    ...dealerServiceAppHistoryItems.documentsReceivedAcknowledgementSent
  ];
  
  const showApplicableCommentsDialogForApplicationSource = (): void => {
    if (props.currentApp.appSource?.toUpperCase() === "DEALER FLOW") {
      openDialog({
        children: (
          <ErrorDialog
            title={"Dealer Flow Analyst Comments"}
            errors={["To send comments within the LOS, please use the chat feature"]}
            closeDialog={closeDialog}
          />
        )
      });
      return; // return early and exit the function
    }
    
    const preDealertrackCommentsErrors: string[] = validatePreDealertrackComments();
    if (props.currentApp && preDealertrackCommentsErrors.length === 0) {
      openDialog({
        children: (
          <NoteDialog
            title={`Send Note to ${props.currentApp.appSource}`}
            accept={(analystComments: AnalystComment[]) => {
              props.dispatch(sendAnalystCommentsToDealerTrack({
                applicationId: props.currentApp.id,
                underwriterId: props.currentApp.applicationAssignments.find(a => a.assignmentEmployeeRoleId === Constants.roles.underwriterId)?.assignedToId!,
                analystComments: analystComments
              }));
              closeDialog();
            }}
            closeDialog={closeDialog}
          />
        )
      });
    } else {
      {
        openDialog({
          children: (
            <ErrorDialog
              title="Decision Validation Errors"
              errors={preDealertrackCommentsErrors}
              closeDialog={closeDialog}
            />
          ),
        });
      }
    }
  }
  
  return (
    <Card>
      <CardHeader
        avatar={<HistoryIcon fontSize="small" />}
        title="Application History"
        action={
          props.currentApp?.appSource?.toUpperCase() !== 'DEALER FLOW' && (
            <Button
              variant="contained"
              color="inherit"
              onClick={() => showApplicableCommentsDialogForApplicationSource()}
            >
              Send Analyst Comments
            </Button>
          )
        }
      />
      <Divider variant="middle" />
      <CardContent>
        <DealerServiceHistoryListView
          historyItems={sort(
            (left: IOrderableType, right: IOrderableType) =>
              new Date(left.createdDate) > new Date(right.createdDate) ? -1
                : new Date(left.createdDate) < new Date(right.createdDate) ? 1
                  : 0
            , historyItems
          )}
          appSource={props.currentApp.appSource}
        />
      </CardContent>
    </Card>
  );
}